/**
 * The public board, as addressed from inside the admin panel.
 *
 * The panel and the site are two different origins — `admin.sustentalent.com`
 * is this backend on Cloud Run, `sustentalent.com` is the Astro build on
 * Firebase Hosting — so nothing in the admin can reach a public page with a
 * relative path. Every "see what a visitor sees" link has to name the host.
 *
 * Hard-coded rather than read from an env var, deliberately. The admin is built
 * by Vite and this module is also imported by collection config that the
 * backend loads under plain Node, so `import.meta.env` here would be undefined
 * on one of the two paths — the kind of difference that shows up as a link
 * reading `undefined/vacantes/…` in production only. The site's domain is
 * already written literally in `welcome-email.ts` and the CORS defaults; this
 * is the same constant, in the one place the panel needs it.
 */
export const PUBLIC_SITE_URL = "https://sustentalent.com";

/**
 * A vacancy's page on the public board, addressed by id.
 *
 * **This URL is not served directly any more, and depends on a deploy.** The
 * site used to rewrite `/vacantes/**` to one static shell that read the last
 * path segment back as an id; it now prerenders a page per vacancy at the slug
 * path `canonicalJobUrl` builds, and `scripts/sync-hosting-redirects.ts` writes
 * one 301 per published vacancy into `firebase.json` so that the id form keeps
 * working. Those redirects are generated at build time, so a vacancy published
 * since the last site deploy has no id URL at all — it is a 404 until the
 * nightly rebuild runs.
 *
 * Which makes this the right link for a panel button (an operator who hits the
 * gap reloads in the morning) and the wrong one for anything permanent. For a
 * link that outlives the click — an email, a LinkedIn post — use
 * `canonicalJobUrl`, which addresses the prerendered page itself.
 *
 * Only meaningful for a `published` vacancy: RLS exposes exactly those to an
 * anonymous reader, so a link to anything else is a "no encontrada" page.
 */
export function publicJobUrl(jobId: string): string {
    return `${PUBLIC_SITE_URL}/vacantes/${encodeURIComponent(jobId)}`;
}

/** How much of the slug survives in the path, before the id suffix. */
const MAX_SLUG_CHARS = 70;

/** Accent-stripped, lowercase, hyphenated. */
function slugify(value: string | null | undefined): string {
    if (!value) return "";
    return value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

/**
 * The page the site actually prerenders for a vacancy: slug, then eight
 * characters of its id.
 *
 * A second copy of the site's `src/lib/job-url.ts` rule, and a deliberate one.
 * That module belongs to the Astro build — a different package, a different
 * tsconfig and a different Docker context — and reaching across for twenty
 * lines is how `admin-app` ended up with two package links pointing above the
 * repo root, which built here and failed on every machine that was not this
 * laptop (41bad452). The duplication is pinned instead:
 * `linkedin-eligibility.test.ts` asserts the same paths the site's own
 * `tests/job-seo.test.ts` asserts.
 *
 * The id suffix is not decoration. Slugs are generated from title and employer
 * and are **not unique** — two Adecco rows on the live board share one — so
 * without it, one of any colliding pair is unreachable.
 *
 * If the two copies ever disagree, this one is wrong by definition: the site
 * decides what it serves.
 */
export function canonicalJobUrl(job: { id: string; slug?: string | null; title?: string | null }): string {
    let base = slugify(job.slug || job.title) || "vacante";
    // Long titles are cut back to a whole word, never mid-word.
    if (base.length > MAX_SLUG_CHARS) {
        base = base.slice(0, MAX_SLUG_CHARS);
        const lastBreak = base.lastIndexOf("-");
        if (lastBreak > 20) base = base.slice(0, lastBreak);
    }
    base = base.replace(/^-+|-+$/g, "");
    const suffix = String(job.id).split("-")[0];
    return `${PUBLIC_SITE_URL}/vacantes/${base}-${suffix}`;
}
